// HRAuthPage.js - Updated design matching AccessRecruitPage
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  MdRocketLaunch,
  MdAutoGraph,
  MdAnalytics,
  MdGroups,
  MdPerson,
  MdTimerOff,
  MdClose,
  MdCheck,
  MdWarning,
  MdError,
  MdLock,
  MdEmail,
  MdPersonOutline,
  MdBusiness,
  MdPeople,
  MdDescription,
  MdSend,
  MdArrowBack,
  MdUpgrade,
  MdArrowForward,
  MdCrown
} from "react-icons/md";
import { FaRocket, FaCrown, FaCheckCircle, FaExclamationCircle, FaLightbulb } from 'react-icons/fa';
import './HRAuthPage.css';

const HRAuthPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const resendTimerRef = useRef(null);
  
  // Get data from location state or query params
  const { isLoginForm: initialIsLoginForm = false, onboardingData = null } = location.state || {};
  
  const [isLoginForm, setIsLoginForm] = useState(initialIsLoginForm);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "", showSuggestion: false, suggestionText: "" });
  const [signupStep, setSignupStep] = useState(false);
  const [signupOtp, setSignupOtp] = useState("");
  const [loginStep, setLoginStep] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const [hasCheckedSubscription, setHasCheckedSubscription] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  
  // Email validation states
  const [emailError, setEmailError] = useState("");
  const [isEmailValid, setIsEmailValid] = useState(false);
  const [emailSuggestion, setEmailSuggestion] = useState("");
  
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    employees: "",
    password: "",
  });
  
  const [loginData, setLoginData] = useState({ 
    email: "", 
    otp: "",
    emailError: "",
    emailSuggestion: ""
  });
  
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:5000";

  // Enhanced email validation function with typo detection
  const validateEmail = (email) => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    
    if (!email) {
      return { 
        isValid: false, 
        error: "Email is required",
        suggestion: ""
      };
    }
    
    if (!emailRegex.test(email)) {
      return { 
        isValid: false, 
        error: "Please enter a valid email address",
        suggestion: ""
      };
    }
    
    // Extract domain
    const domain = email.split('@')[1].toLowerCase();
    const username = email.split('@')[0];
    
    // Check for common typos in popular domains
    const commonTypos = {
      'gmal.com': 'gmail.com',
      'gmai.com': 'gmail.com',
      'gail.com': 'gmail.com',
      'gmil.com': 'gmail.com',
      'gil.com':'gmail.com',
      'gal.com':'gmail.com',
      'gmaill.com': 'gmail.com',
      'gnail.com': 'gmail.com',
      'gamil.com': 'gmail.com',
      'yaho.com': 'yahoo.com',
      'yhoo.com': 'yahoo.com',
      'yaoo.com': 'yahoo.com',
      'yahu.com': 'yahoo.com',
      'outloo.com': 'outlook.com',
      'outlok.com': 'outlook.com',
      'outllok.com': 'outlook.com',
      'hotmai.com': 'hotmail.com',
      'hotmil.com': 'hotmail.com',
      'hotmal.com': 'hotmail.com',
      'hotmaiil.com': 'hotmail.com'
    };
    
    // Check for typo
    if (commonTypos[domain]) {
      const correctedEmail = `${username}@${commonTypos[domain]}`;
      return { 
        isValid: false, 
        error: `"${domain}" is not a valid domain.`,
        suggestion: `Did you mean ${commonTypos[domain]}?`,
        correctedEmail: correctedEmail
      };
    }
    
    // Check for suspicious domains (missing dot)
    const suspiciousDomains = [
      'gmal', 'gmai', 'gail', 'gmil', 'yaho', 'outloo', 'hotmai'
    ];
    
    for (let suspicious of suspiciousDomains) {
      if (domain === suspicious) {
        return { 
          isValid: false, 
          error: `"${domain}" is not a complete domain.`,
          suggestion: `Did you mean ${suspicious}.com?`,
          correctedEmail: `${username}@${suspicious}.com`
        };
      }
    }
    
    // Check for disposable email domains
    const disposableDomains = [
      'tempmail.com', 'mailinator.com', 'guerrillamail.com', '10minutemail.com',
      'throwawaymail.com', 'yopmail.com', 'fakeinbox.com', 'temp-mail.org',
      'sharklasers.com', 'guerrillamail.biz', 'guerrillamail.org', 'grr.la',
      'pokemail.net', 'spam4.me', 'getairmail.com', 'trashmail.com',
      'dispostable.com', 'mailnesia.com', 'maildrop.cc'
    ];
    
    if (disposableDomains.some(disposable => domain.includes(disposable))) {
      return { 
        isValid: false, 
        error: "Disposable/temporary emails are not allowed.",
        suggestion: "Please use your work email.",
        correctedEmail: ""
      };
    }
    
    // Check for invalid TLDs (Top Level Domains)
    const validTLDs = [
      'com', 'in', 'org', 'net', 'edu', 'gov', 'co', 'io', 'ai', 'me',
      'info', 'biz', 'us', 'uk', 'ca', 'au', 'de', 'fr', 'es', 'it',
      'jp', 'cn', 'ru', 'br', 'mx', 'ar', 'za', 'nz', 'sg', 'my',
      'co.in', 'co.uk', 'com.au', 'com.sg', 'org.in', 'net.in'
    ];
    
    const tld = domain.split('.').pop().toLowerCase();
    if (!validTLDs.includes(tld)) {
      return { 
        isValid: false, 
        error: "Invalid domain extension.",
        suggestion: "Please use a valid email domain.",
        correctedEmail: ""
      };
    }
    
    // Check for work email patterns (company emails)
    const workEmailPatterns = [
      /\.(co|inc|llc|corp|tech|solutions|consulting|digital|group|global|ventures|capital|partners|holdings|enterprises|industries|ltd|gmbh|pte|plc)\.[a-z]{2,}$/i
    ];
    
    const isLikelyWorkEmail = workEmailPatterns.some(pattern => pattern.test(domain));
    
    return { 
      isValid: true, 
      error: "",
      suggestion: isLikelyWorkEmail ? "✓ Valid work email" : "",
      correctedEmail: ""
    };
  };

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (resendTimerRef.current) {
        clearInterval(resendTimerRef.current);
      }
    };
  }, []);

  // Check existing subscription status - UPDATED ENDPOINT
  useEffect(() => {
    const checkSubscriptionStatus = async () => {
      const token = sessionStorage.getItem("token");
      const userEmail = sessionStorage.getItem("userEmail");
      
      console.log('🔍 Checking HR subscription status:', { 
        hasToken: !!token, 
        hasEmail: !!userEmail 
      });

      if (token && userEmail) {
        try {
          // Updated endpoint from /api/hr/subscription-status to /api/payment/hr-subscription-status
          const response = await fetch(`${API_BASE_URL}/api/payment/hr-subscription-status`, {
            method: "GET",
            headers: { 
              "Content-Type": "application/json",
              "Authorization": `Bearer ${token}`
            }
          });
          
          const data = await response.json();
          
          if (response.ok && data.success) {
            console.log('📊 HR Subscription status:', data.data);
            setSubscriptionStatus(data.data);
            
            // If user has active subscription or trial, redirect to dashboard
            if (data.data.hasActiveAccess || data.data.isTrialActive) {
              console.log('✅ User has active HR access, redirecting to HR dashboard');
              navigate("/dashboard");
              return;
            }
            
            // If trial ended but not paid, show trial ended state
            if (data.data.trialEnded && !data.data.hasPaidSubscription) {
              console.log('❌ HR Trial ended, showing upgrade option');
              // We'll handle this in the UI
            }
          }
        } catch (error) {
          console.error('Subscription check error:', error);
        }
      }
      setHasCheckedSubscription(true);
    };

    checkSubscriptionStatus();
  }, [navigate, API_BASE_URL]);

  // Reset states when component mounts or data changes
  useEffect(() => {
    setIsLoginForm(initialIsLoginForm);
    setSignupStep(false);
    setLoginStep(false);
    setFormData({
      name: "",
      email: "",
      company: "",
      employees: "",
      password: "",
    });
    setLoginData({ email: "", otp: "", emailError: "", emailSuggestion: "" });
    setSignupOtp("");
    setMessage({ type: "", text: "", showSuggestion: false, suggestionText: "" });
    setEmailError("");
    setEmailSuggestion("");
    setIsEmailValid(false);
    
    // Pre-fill data if onboardingData exists
    if (onboardingData) {
      const email = onboardingData.candidateEmail || '';
      const validation = validateEmail(email);
      
      setFormData(prev => ({
        ...prev,
        email: email,
        name: onboardingData.candidateName || '',
        company: onboardingData.companyName || ''
      }));
      
      if (validation.isValid) {
        setIsEmailValid(true);
      }
      setEmailError(validation.error);
      setEmailSuggestion(validation.suggestion);
    }
  }, [initialIsLoginForm, onboardingData]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    
    // Real-time email validation for signup
    if (name === "email") {
      const validation = validateEmail(value);
      setEmailError(validation.error);
      setEmailSuggestion(validation.suggestion);
      setIsEmailValid(validation.isValid);
    }
  };

  const handleLoginInputChange = (e) => {
    const { name, value } = e.target;
    
    if (name === "email") {
      const validation = validateEmail(value);
      setLoginData(prev => ({ 
        ...prev, 
        [name]: value,
        emailError: validation.error,
        emailSuggestion: validation.suggestion
      }));
    } else {
      setLoginData({ ...loginData, [name]: value });
    }
  };

  const startResendTimer = () => {
    if (resendTimerRef.current) {
      clearInterval(resendTimerRef.current);
    }
    
    setResendTimer(60);
    resendTimerRef.current = setInterval(() => {
      setResendTimer((prev) => {
        if (prev <= 1) {
          clearInterval(resendTimerRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const showMessage = (type, text, suggestion = "") => {
    setMessage({ 
      type, 
      text, 
      showSuggestion: !!suggestion,
      suggestionText: suggestion 
    });
    setTimeout(() => setMessage({ 
      type: "", 
      text: "", 
      showSuggestion: false, 
      suggestionText: "" 
    }), 5000);
  };

  const handleApplySuggestion = () => {
    if (isLoginForm) {
      if (loginData.email.includes('gmal.com')) {
        const correctedEmail = loginData.email.replace('gmal.com', 'gmail.com');
        setLoginData(prev => ({ 
          ...prev, 
          email: correctedEmail,
          emailError: "",
          emailSuggestion: ""
        }));
      }
    } else {
      if (formData.email.includes('gmal.com')) {
        const correctedEmail = formData.email.replace('gmal.com', 'gmail.com');
        setFormData(prev => ({ 
          ...prev, 
          email: correctedEmail
        }));
        setEmailError("");
        setEmailSuggestion("");
        setIsEmailValid(true);
      }
    }
    setMessage({ type: "", text: "", showSuggestion: false, suggestionText: "" });
  };

  const handleAuthSwitch = () => {
    setIsLoginForm(!isLoginForm);
    setSignupStep(false);
    setLoginStep(false);
    setFormData({
      name: "",
      email: "",
      company: "",
      employees: "",
      password: "",
    });
    setLoginData({ email: "", otp: "", emailError: "", emailSuggestion: "" });
    setSignupOtp("");
    setMessage({ type: "", text: "", showSuggestion: false, suggestionText: "" });
    setEmailError("");
    setEmailSuggestion("");
    setIsEmailValid(false);
  };

  // Enhanced Signup Flow with Email Validation
  const handleSignupSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setMessage({ type: "", text: "", showSuggestion: false, suggestionText: "" });

    // Validate email before proceeding
    const emailValidation = validateEmail(formData.email);
    if (!emailValidation.isValid) {
      showMessage("error", emailValidation.error, emailValidation.suggestion);
      setIsSubmitting(false);
      return;
    }

    // Password validation
    if (formData.password.length < 8) {
      showMessage("error", "Password must be at least 8 characters long");
      setIsSubmitting(false);
      return;
    }

    // Check for weak passwords
    const weakPasswordPatterns = [
      /^[a-zA-Z]+$/, // Only letters
      /^[0-9]+$/, // Only numbers
      /^[a-z]+$/, // Only lowercase
      /^[A-Z]+$/, // Only uppercase
      /^(.)\1+$/ // Repeated characters
    ];
    
    if (weakPasswordPatterns.some(pattern => pattern.test(formData.password))) {
      showMessage("error", "Password is too weak. Use a mix of letters, numbers, and special characters.");
      setIsSubmitting(false);
      return;
    }

    try {
      if (!signupStep) {
        // Validate all fields
        if (!formData.name || !formData.company || !formData.employees) {
          showMessage("error", "Please fill all required fields");
          setIsSubmitting(false);
          return;
        }

        const res = await fetch(`${API_BASE_URL}/api/auth/signup-send-otp`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            ...formData,
            onboardingData: onboardingData,
            userType: "hr_manager"
          }),
        });
        const data = await res.json();
        if (!res.ok) {
          // Check if it's an email validation error from backend
          if (data.code === 'INVALID_EMAIL_FORMAT' || data.error?.includes('email')) {
            showMessage("error", data.error || "Invalid email format");
          } else {
            throw new Error(data.error || "Failed to send OTP");
          }
          return;
        }

        setSignupStep(true);
        startResendTimer();
        showMessage("success", "OTP sent to your email. Please verify.");
      } else {
        const res = await fetch(`${API_BASE_URL}/api/auth/signup-verify-otp`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ 
            ...formData, 
            otp: signupOtp,
            onboardingData: onboardingData,
            userType: "hr_manager",
            startTrial: true
          }),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || "Invalid OTP");

        // Store all necessary data
        sessionStorage.setItem("token", data.token);
        sessionStorage.setItem("companyId", data.companyId);
        sessionStorage.setItem("companyName", formData.company);
        sessionStorage.setItem("userName", formData.name);
        sessionStorage.setItem("userEmail", formData.email);
        sessionStorage.setItem("userType", "hr_manager");
        
        if (data.subscription) {
          sessionStorage.setItem("subscription", JSON.stringify(data.subscription));
        }
        
        if (onboardingData) {
          sessionStorage.setItem("onboardingData", JSON.stringify(onboardingData));
        }

        showMessage("success", onboardingData 
          ? "Account created! Starting onboarding..." 
          : "🎉 Account created! Starting your 30-day free trial..."
        );
        
        setTimeout(() => {
          if (onboardingData) {
            navigate("/onboarding", { 
              state: { 
                onboardingData: onboardingData,
                newUser: true 
              } 
            });
          } else {
            if (data.subscription?.isTrialActive) {
              navigate("/dashboard");
            } else if (data.subscription?.trialEnded && !data.subscription?.isPaid) {
              // Redirect to HR pricing page with PayPal integration
              navigate("/hr-pricing", { 
                state: { 
                  fromTrialEnded: true,
                  userEmail: formData.email
                } 
              });
            } else {
              navigate("/dashboard");
            }
          }
        }, 1000);
      }
    } catch (err) {
      showMessage("error", err.message);
      
      if (err.message.includes('trial ended') || err.message.includes('Trial period')) {
        const targetEmail = formData.email;
        sessionStorage.setItem("trialEndedEmail", targetEmail);
        // Redirect to HR pricing page with PayPal integration
        navigate("/hr-pricing", { 
          state: { 
            fromTrialEnded: true,
            userEmail: targetEmail
          } 
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Enhanced Login Flow with Email Validation
  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setMessage({ type: "", text: "", showSuggestion: false, suggestionText: "" });

    // Validate email before proceeding
    const emailValidation = validateEmail(loginData.email);
    if (!emailValidation.isValid) {
      showMessage("error", emailValidation.error, emailValidation.suggestion);
      setIsSubmitting(false);
      return;
    }

    try {
      if (!loginStep) {
        const res = await fetch(`${API_BASE_URL}/api/auth/login-send-otp`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ 
            email: loginData.email,
            userType: "hr_manager"
          }),
        });
        const data = await res.json();
        if (!res.ok) {
          // Check if it's an email validation error from backend
          if (data.code === 'INVALID_EMAIL_FORMAT' || data.error?.includes('email')) {
            showMessage("error", data.error || "Invalid email format");
          } else {
            throw new Error(data.error || "Failed to send OTP");
          }
          return;
        }

        setLoginStep(true);
        startResendTimer();
        showMessage("success", "OTP sent to your email.");
      } else {
        const res = await fetch(`${API_BASE_URL}/api/auth/login-verify-otp`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            ...loginData,
            userType: "hr_manager"
          }),
        });
        const data = await res.json();
        if (!res.ok) {
          if (data.code === 'TRIAL_ENDED' || data.code === 'ACCESS_ENDED') {
            const userEmail = data.userEmail || loginData.email;
            sessionStorage.setItem("trialEndedEmail", userEmail);
            // Redirect to HR pricing page with PayPal integration
            navigate("/hr-pricing", { 
              state: { 
                fromTrialEnded: true,
                userEmail: userEmail
              } 
            });
            return;
          }
          throw new Error(data.error || "Invalid OTP");
        }

        // Store user data
        sessionStorage.setItem("token", data.token);
        sessionStorage.setItem("companyId", data.companyId);
        sessionStorage.setItem("companyName", data.companyName);
        sessionStorage.setItem("userName", data.userName);
        sessionStorage.setItem("userEmail", loginData.email);
        sessionStorage.setItem("userType", "hr_manager");
        
        if (data.subscription) {
          sessionStorage.setItem("subscription", JSON.stringify(data.subscription));
        }

        showMessage("success", "Login successful!");
        
        if (data.subscription) {
          const { isTrialActive, hasActiveAccess, trialEnded, isPaid } = data.subscription;
          
          if (trialEnded && !isPaid) {
            // Redirect to HR pricing page with PayPal integration
            navigate("/hr-pricing", { 
              state: { 
                fromTrialEnded: true,
                userEmail: loginData.email
              } 
            });
          } else if (isTrialActive || hasActiveAccess) {
            setTimeout(() => navigate("/dashboard"), 1000);
          } else {
            // Redirect to HR pricing page with PayPal integration
            navigate("/hr-pricing", { 
              state: { 
                userEmail: loginData.email
              } 
            });
          }
        } else {
          setTimeout(() => navigate("/dashboard"), 1000);
        }
      }
    } catch (err) {
      showMessage("error", err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Resend OTP
  const handleResendOtp = async (type) => {
    setIsSubmitting(true);
    setMessage({ type: "", text: "", showSuggestion: false, suggestionText: "" });

    try {
      const endpoint = `${API_BASE_URL}/api/auth/resend-otp`;
      const body = type === "signup" ? { 
        email: formData.email,
        userType: "hr_manager"
      } : { 
        email: loginData.email,
        userType: "hr_manager"
      };

      // Validate email before resending
      const emailValidation = validateEmail(body.email);
      if (!emailValidation.isValid) {
        showMessage("error", emailValidation.error, emailValidation.suggestion);
        setIsSubmitting(false);
        return;
      }

      const res = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      const data = await res.json();
      
      if (data.success && data.otp) {
        console.log(`📧 Test OTP for ${body.email}: ${data.otp}`);
        startResendTimer();
        showMessage("success", `New OTP sent! Test OTP: ${data.otp}`);
      } else if (data.success) {
        startResendTimer();
        showMessage("success", "New OTP sent to your email!");
      } else {
        throw new Error(data.error || "Failed to resend OTP");
      }
      
    } catch (err) {
      showMessage("error", err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBackToHome = () => {
    navigate("/");
  };

  const handleGoToPricing = () => {
    const targetEmail = isLoginForm ? loginData.email : formData.email;
    
    if (targetEmail) {
      sessionStorage.setItem("trialEndedEmail", targetEmail);
    }
    
    // Navigate to the new HR pricing page with PayPal integration
    navigate("/hr-pricing", { 
      state: { 
        userEmail: targetEmail
      } 
    });
  };

  const formatTimer = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
  };

  const handleBack = () => {
    if (isLoginForm) {
      setLoginStep(false);
      setLoginData(prev => ({ ...prev, otp: "" }));
    } else {
      setSignupStep(false);
      setSignupOtp("");
    }
    setMessage({ type: "", text: "", showSuggestion: false, suggestionText: "" });
    
    if (resendTimerRef.current) {
      clearInterval(resendTimerRef.current);
    }
    setResendTimer(0);
  };

  if (!hasCheckedSubscription) {
    return (
      <div className="hr-auth-page-loading">
        <div className="hr-auth-page-spinner"></div>
        <p>Checking your account status...</p>
      </div>
    );
  }

  if (subscriptionStatus?.trialEnded && !subscriptionStatus?.isPaid) {
    return (
      <div className="hr-auth-trial-ended-container">
        <div className="hr-auth-trial-ended-content">
          <div className="hr-auth-trial-ended-icon"><MdTimerOff size={48} /></div>
          <h2 className="hr-auth-trial-ended-title">Trial Period Ended</h2>
          <p className="hr-auth-trial-ended-message">
            Your free trial has ended. Please upgrade to continue using HR services.
          </p>
          <button
            onClick={handleGoToPricing}
            className="hr-auth-upgrade-button"
          >
            <MdUpgrade size={20} /> Upgrade Now
          </button>
          <button
            onClick={() => {
              setSubscriptionStatus(null);
              setHasCheckedSubscription(true);
            }}
            className="hr-auth-back-button"
          >
            <MdArrowBack size={20} /> Back to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="hrcp-auth-container">
      <div className={`hrcp-auth-content-container ${isLoginForm ? "layout-login" : "layout-signup"}`}>
        {/* Left Section - Login Form Card */}
        <div className="hrcp-auth-left">
          <div className="hrcp-auth-form-wrapper">
            {/* Logo Section - Inside card, only for initial form (before OTP) */}
            {!(loginStep || signupStep) && (
              <div className="hrcp-logo-container">
                <div className="quantum-nav__logo">
                  <div className="quantum-nav__logo-orb">
                    <img 
                      src="/images/logo415.png" 
                      alt="Logo" 
                      className="quantum-nav__logo-img" 
                    />
                    <span className="Humanex_logo_title">umanex</span>
                  </div>
                </div>
              </div>
            )}
            
            {message.text && (message.text.includes('trial ended') || message.text.includes('Trial period')) && (
              <div className="hrcp-trial-ended-banner hrcp-alert hrcp-alert-warning">
                <div className="hrcp-banner-icon"><MdUpgrade size={24} /></div>
                <div className="hrcp-banner-content">
                  <h4>Upgrade Required</h4>
                  <p>Your free trial has ended. Upgrade to continue using our services.</p>
                </div>
              </div>
            )}

            {!(loginStep || signupStep) ? (
              <div className={`form-content-wrapper ${!isLoginForm ? 'scrollable-content' : ''}`}>
                <div className="hrcp-auth-form-header">
                  <h1 className="hrcp-form-title">
                    {onboardingData ? "Complete Your Registration" : 
                      isLoginForm ? "Welcome back " : "Create an HR Account"}
                  </h1>
                  <p className="hrcp-form-subtitle">
                    {onboardingData 
                      ? "Create your account to begin your onboarding journey"
                      : isLoginForm
                        ? "Sign in to continue your journey"
                        : "Start your 30-day free trial"
                    }
                  </p>
                </div>

                <div className="hrcp-auth-toggle">
                  <button
                    className={`hrcp-toggle-btn ${isLoginForm ? 'hrcp-active' : ''}`}
                    onClick={() => handleAuthSwitch()}
                    type="button"
                  >
                    Login
                  </button>
                  <button
                    className={`hrcp-toggle-btn ${!isLoginForm ? 'hrcp-active' : ''}`}
                    onClick={() => handleAuthSwitch()}
                    type="button"
                  >
                    Sign Up
                  </button>
                </div>

                {message.text && !message.text.includes('trial ended') && !message.text.includes('Trial period') && (
                  <div className={`hrcp-alert ${message.type === "error" ? 'hrcp-alert-error' : 'hrcp-alert-success'}`}>
                    <span className="hrcp-alert-icon">
                      {message.type === "error" ? <MdError size={20} /> : <MdCheck size={20} />}
                    </span>
                    <span>{message.text}</span>
                  </div>
                )}

                <form className="hrcp-auth-form" onSubmit={isLoginForm ? handleLoginSubmit : handleSignupSubmit}>
                  {isLoginForm ? (
                    <>
                      <div className="hrcp-form-group">
                        <label className="hrcp-form-label">
                          <MdEmail size={16} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                          Email Address
                        </label>
                        <input
                          type="email"
                          className="hrcp-form-input"
                          name="email"
                          value={loginData.email}
                          onChange={handleLoginInputChange}
                          placeholder="name@company.com"
                          required
                          disabled={loginStep || isSubmitting}
                        />
                        {loginData.emailError && (
                          <div className="hrcp-form-error">
                            <MdError size={14} /> {loginData.emailError}
                            {loginData.email.includes('gmal.com') && (
                              <button 
                                type="button"
                                className="hrcp-suggestion-btn"
                                onClick={handleApplySuggestion}
                              >
                                <FaLightbulb /> Fix to gmail.com
                              </button>
                            )}
                          </div>
                        )}
                        {loginData.emailSuggestion && !loginData.emailError && (
                          <div className="hrcp-form-suggestion">
                            <FaLightbulb /> {loginData.emailSuggestion}
                          </div>
                        )}
                      </div>

                      <div className="form-buttons-container">
                        <button
                          type="submit"
                          className="hrcp-btn-primary"
                          disabled={isSubmitting || (!loginData.email && !validateEmail(loginData.email).isValid)}
                        >
                          {isSubmitting ? <span className="hrcp-spinner"></span> : (
                            <>
                              <MdSend size={18} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                              Continue
                            </>
                          )}
                        </button>

                        {(message.text && (message.text.includes('trial ended') || message.text.includes('Trial period'))) && (
                          <button
                            type="button"
                            className="hrcp-btn-pricing"
                            onClick={handleGoToPricing}
                          >
                            <MdUpgrade size={18} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                            Upgrade Now
                          </button>
                        )}

                        <button
                          type="button"
                          className="hrcp-btn-text"
                          onClick={handleAuthSwitch}
                        >
                          {!isLoginForm ? "Already have an account? Log in" : "Don't have an account? Sign up for free trial"}
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="form-scroll-area">
                        <div className="hrcp-form-row">
                          <div className="hrcp-form-group">
                            <label className="hrcp-form-label">
                              <MdPersonOutline size={16} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                              Full Name
                            </label>
                            <input
                              type="text"
                              className="hrcp-form-input"
                              name="name"
                              value={formData.name}
                              onChange={handleInputChange}
                              placeholder="John Doe"
                              required
                              disabled={signupStep || isSubmitting}
                            />
                          </div>

                          <div className="hrcp-form-group">
                            <label className="hrcp-form-label">
                              <MdEmail size={16} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                              Email Address
                            </label>
                            <input
                              type="email"
                              className="hrcp-form-input"
                              name="email"
                              value={formData.email}
                              onChange={handleInputChange}
                              placeholder="name@company.com"
                              required
                              disabled={signupStep || isSubmitting}
                            />
                            {emailError && (
                              <div className="hrcp-form-error">
                                <MdError size={14} /> {emailError}
                                {formData.email.includes('gmal.com') && (
                                  <button 
                                    type="button"
                                    className="hrcp-suggestion-btn"
                                    onClick={handleApplySuggestion}
                                  >
                                    <FaLightbulb /> Fix to gmail.com
                                  </button>
                                )}
                              </div>
                            )}
                            {emailSuggestion && !emailError && (
                              <div className="hrcp-form-suggestion">
                                <FaLightbulb /> {emailSuggestion}
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="hrcp-form-group">
                          <label className="hrcp-form-label">
                            <MdBusiness size={16} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                            Company Name
                          </label>
                          <input
                            type="text"
                            className="hrcp-form-input"
                            name="company"
                            value={formData.company}
                            onChange={handleInputChange}
                            placeholder="Your Company"
                            required
                            disabled={signupStep || isSubmitting}
                          />
                        </div>

                        <div className="hrcp-form-group">
                          <label className="hrcp-form-label">
                            <MdPeople size={16} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                            Company Size
                          </label>
                          <select
                            className="hrcp-form-input"
                            name="employees"
                            value={formData.employees}
                            onChange={handleInputChange}
                            required
                            disabled={signupStep || isSubmitting}
                          >
                            <option value="">Select Company Size</option>
                            <option value="1-50">1-50 employees</option>
                            <option value="51-200">51-200 employees</option>
                            <option value="201-500">201-500 employees</option>
                            <option value="501-1000">501-1000 employees</option>
                            <option value="1000+">1000+ employees</option>
                          </select>
                        </div>

                        <div className="hrcp-form-group">
                          <label className="hrcp-form-label">
                            <MdLock size={16} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                            Password
                          </label>
                          <input
                            type="password"
                            className="hrcp-form-input"
                            name="password"
                            value={formData.password}
                            onChange={handleInputChange}
                            placeholder="Password (min. 8 characters)"
                            required
                            minLength="8"
                            disabled={signupStep || isSubmitting}
                          />
                          <div className="hrcp-password-hint">
                            Use a mix of letters, numbers, and special characters
                          </div>
                        </div>
                      </div>

                      <div className="form-buttons-container">
                        <button
                          type="submit"
                          className="hrcp-btn-primary"
                          disabled={isSubmitting || !isEmailValid}
                        >
                          {isSubmitting ? <span className="hrcp-spinner"></span> : (
                            <>
                              <MdRocketLaunch size={18} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                              Start Free Trial
                            </>
                          )}
                        </button>

                        <button
                          type="button"
                          className="hrcp-btn-text"
                          onClick={handleAuthSwitch}
                        >
                          Already have an account? Log in
                        </button>
                      </div>
                    </>
                  )}
                </form>
              </div>
            ) : (
              <div className="form-content-wrapper">
                <div className="hrcp-otp-header">
                  <h3>Check your email</h3>
                  <p>
                    We sent a verification code to{' '}
                    <span className="hrcp-otp-email">
                      {isLoginForm ? loginData.email : formData.email}
                    </span>
                  </p>

                  <div className="hrcp-resend-otp">
                    <p className="hrcp-resend-text">
                      Didn't receive the code?
                      {resendTimer > 0 ? (
                        <span className="hrcp-resend-timer">
                          Resend available in {formatTimer(resendTimer)}
                        </span>
                      ) : (
                        <button
                          type="button"
                          className="hrcp-resend-link"
                          onClick={() => handleResendOtp(isLoginForm ? "login" : "signup")}
                          disabled={isSubmitting}
                        >
                          Resend OTP
                        </button>
                      )}
                    </p>
                  </div>

                  {!isLoginForm && (
                    <div className="hrcp-trial-badge">
                      <span className="hrcp-badge-icon"><MdRocketLaunch size={18} /></span>
                      <span>30-Day Free Trial</span>
                    </div>
                  )}
                </div>

                {message.text && (
                  <div className={`hrcp-alert ${message.type === "error" ? 'hrcp-alert-error' : 'hrcp-alert-success'}`}>
                    <span className="hrcp-alert-icon">
                      {message.type === "error" ? <MdError size={20} /> : <MdCheck size={20} />}
                    </span>
                    <span>{message.text}</span>
                  </div>
                )}

                <form className="hrcp-auth-form" onSubmit={isLoginForm ? handleLoginSubmit : handleSignupSubmit}>
                  <div className="hrcp-form-group">
                    <label className="hrcp-form-label">
                      <MdLock size={16} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                      Verification Code
                    </label>
                    <input
                      type="text"
                      className="hrcp-form-input hrcp-otp-input"
                      name={isLoginForm ? "otp" : ""}
                      value={isLoginForm ? loginData.otp : signupOtp}
                      onChange={isLoginForm ? handleLoginInputChange : (e) => setSignupOtp(e.target.value)}
                      placeholder="Enter 6-digit code"
                      maxLength={6}
                      required
                      disabled={isSubmitting}
                    />
                  </div>

                  <div className="form-buttons-container">
                    <button
                      type="submit"
                      className="hrcp-btn-primary"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? <span className="hrcp-spinner"></span> : (
                        <>
                          <MdCheck size={18} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                          Verify & Continue
                        </>
                      )}
                    </button>

                    {(message.text && (message.text.includes('trial ended') || message.text.includes('Trial period'))) && (
                      <button
                        type="button"
                        className="hrcp-btn-pricing"
                        onClick={handleGoToPricing}
                      >
                        <MdUpgrade size={18} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                        Upgrade Now
                      </button>
                    )}

                    <button
                      type="button"
                      className="hrcp-btn-secondary"
                      onClick={handleBack}
                    >
                      <MdArrowBack size={18} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                      Use different email
                    </button>
                  </div>
                </form>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default HRAuthPage;